import { useEffect, useState, useRef, useContext } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Grid,
  Container,
  Skeleton,
  Alert,
  Button,
  Chip,
  Paper,
  TextField,
  MenuItem,
  Pagination,
  alpha,
  useTheme,
  InputAdornment,
  Slider,
  Popover,
  Select,
  FormControl,
  InputLabel,
  Badge,
  Divider,
  Stack,
  IconButton,
  Snackbar,
  Fab,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import api from "../../api/api";

import SearchIcon from "@mui/icons-material/Search";
import SpeedIcon from "@mui/icons-material/Speed";
import SortIcon from "@mui/icons-material/Sort";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LocalGasStationIcon from "@mui/icons-material/LocalGasStation";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SellIcon from "@mui/icons-material/Sell";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import GarageIcon from "@mui/icons-material/Garage";
import StarIcon from "@mui/icons-material/Star";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";

import { AuthContext, useAuth } from "../../context/AuthContext";
import { useCompare } from "../../context/CompareContext";
import { useCart } from "../../context/CartContext";
import { useFilters } from "../../context/FilterContext";

import { formatPrice } from "../../utils/formatters";
import SeoComponent from "../Common/SEO";
import StructuredData from "../Common/StructuredData";

// ============================================
// STYLED COMPONENTS
// ============================================

// Hero Carousel
const HeroWrapper = styled(Box)({
  width: "100%",
  margin: 0,
  padding: 0,
  position: "relative",
  overflow: "hidden",
});

const HeroSection = styled(Box)(({ theme }) => ({
  width: "100%",
  position: "relative",
  background:
    theme.palette.mode === "light"
      ? `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.secondary.main, 0.03)} 100%)`
      : `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.background.paper, 0.5)} 100%)`,
  padding: "80px 0 60px",
  overflow: "hidden",
  minHeight: 500,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    background:
      theme.palette.mode === "light"
        ? "transparent"
        : `linear-gradient(to bottom, ${alpha(theme.palette.background.default, 0.7)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
    zIndex: 1,
  },
  "& > *": {
    position: "relative",
    zIndex: 2,
  },
}));

const HeroBackground = styled("img")(({ theme }) => ({
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center 40%",
  filter:
    theme.palette.mode === "light"
      ? "brightness(0.95) saturate(0.9)"
      : "brightness(0.4) contrast(1.1)",
  opacity: theme.palette.mode === "light" ? 0.15 : 0.5,
  zIndex: 0,
  transition: "opacity 0.5s ease",
}));

// Carousel Navigation
const CarouselDot = styled(Box)(({ theme, active }) => ({
  width: active ? 32 : 10,
  height: 10,
  borderRadius: 5,
  backgroundColor: active
    ? theme.palette.primary.main
    : alpha(theme.palette.text.primary, 0.3),
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: active
      ? theme.palette.primary.main
      : alpha(theme.palette.text.primary, 0.5),
  },
}));

const CarouselArrow = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 10,
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  color: theme.palette.text.primary,
  width: 48,
  height: 48,
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  "&:hover": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [theme.breakpoints.down("sm")]: {
    width: 40,
    height: 40,
  },
}));

// Filter Bar
const FilterBar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 16,
  background:
    theme.palette.mode === "light"
      ? theme.palette.background.paper
      : alpha(theme.palette.background.paper, 0.8),
  backdropFilter: "blur(20px)",
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  marginTop: -50,
  marginBottom: theme.spacing(5),
  position: "relative",
  zIndex: 20,
  boxShadow:
    theme.palette.mode === "light"
      ? "0 8px 32px rgba(15, 23, 42, 0.08)"
      : "0 8px 32px rgba(0, 0, 0, 0.25)",
}));

// Car Card
const CarCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  borderRadius: 16,
  overflow: "hidden",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  boxShadow:
    theme.palette.mode === "light"
      ? "0 2px 12px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.04)"
      : "0 2px 12px rgba(0, 0, 0, 0.3)",
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  background: theme.palette.background.paper,
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow:
      theme.palette.mode === "light"
        ? "0 12px 40px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.05)"
        : "0 12px 40px rgba(0, 0, 0, 0.4)",
    borderColor: alpha(theme.palette.primary.main, 0.15),
    "& .car-image": {
      transform: "scale(1.05)",
    },
    "& .view-btn": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      borderColor: theme.palette.primary.main,
    },
  },
}));

// Car Image
const CarImage = styled(CardMedia)({
  height: "220px !important",
  minHeight: "220px !important",
  maxHeight: "220px !important",
  width: "100%",
  objectFit: "cover",
  flexShrink: 0,
  transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
});

// Price Badge
const PriceBadge = styled(Box)(({ theme }) => ({
  background: theme.palette.secondary.main,
  color: theme.palette.common.white,
  padding: "8px 16px",
  borderRadius: 10,
  fontWeight: 800,
  fontSize: "0.9rem",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  transition: "all 0.25s ease",
  whiteSpace: "nowrap",
  letterSpacing: "-0.2px",
}));

// Spec Badge
const SpecBadge = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  color: theme.palette.text.secondary,
  fontSize: "0.85rem",
  fontWeight: 600,
  background:
    theme.palette.mode === "light"
      ? alpha(theme.palette.secondary.main, 0.04)
      : alpha(theme.palette.divider, 0.08),
  padding: "8px 12px",
  borderRadius: 10,
  transition: "all 0.2s ease",
}));

// Service Card
const ServiceCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: 12,
  height: "100%",
  background:
    theme.palette.mode === "light"
      ? theme.palette.background.paper
      : alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  transition: "all 0.25s ease",
  boxShadow: "none",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      theme.palette.mode === "light"
        ? `0 6px 20px ${alpha(theme.palette.secondary.main, 0.08)}`
        : `0 6px 20px rgba(0, 0, 0, 0.2)`,
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

// Icon Wrapper
const IconWrapper = styled(Box)(({ theme, color }) => ({
  width: 56,
  height: 56,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginLeft: "auto",
  marginRight: "auto",
  marginBottom: theme.spacing(2.5),
  background: alpha(color || theme.palette.primary.main, 0.1),
  color: color || theme.palette.primary.main,
}));

const CompareFloatingButton = styled(Box)(({ theme, show }) => ({
  position: "fixed",
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  zIndex: 1000,
  transition: "transform 0.3s ease-in-out",
  transform: show ? "translateY(0)" : "translateY(100px)",
  opacity: show ? 1 : 0,
  pointerEvents: show ? "auto" : "none",
}));

// ============================================
// UTILITY FUNCTIONS
// ============================================

const getTranslatedTransmission = (transmission, t) => {
  if (!transmission) return "N/C";
  const lower = transmission.toLowerCase();
  if (lower.includes("auto") || lower === "a") {
    return t("car.transmission.auto");
  } else if (lower.includes("man") || lower === "m") {
    return t("car.transmission.manual");
  }
  return transmission;
};

const getTranslatedFuelType = (fuelType, t) => {
  if (!fuelType) return "N/C";
  const lower = fuelType.toLowerCase();
  if (lower.includes("essence") || lower === "gas" || lower === "gasoline") {
    return t("car.fuelType.essence");
  } else if (lower === "diesel") {
    return t("car.fuelType.diesel");
  } else if (lower === "electric" || lower.includes("electr")) {
    return t("car.fuelType.electric");
  } else if (lower === "hybrid" || lower === "hybride") {
    return t("car.fuelType.hybrid");
  }
  return fuelType;
};

// ============================================
// MAIN COMPONENT
// ============================================

// Hero Carousel Slides Data
const heroSlides = [
  {
    id: 1,
    badge: "hero.newArrivals",
    title1: "hero.slide1.title1",
    title2: "hero.slide1.title2",
    subtitle: "hero.slide1.subtitle",
    image:
      "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
  },
  {
    id: 2,
    badge: "hero.quality",
    title1: "hero.slide2.title1",
    title2: "hero.slide2.title2",
    subtitle: "hero.slide2.subtitle",
    image:
      "https://images.unsplash.com/photo-1583121274602-3e2820c69888?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
  },
  {
    id: 3,
    badge: "hero.service",
    title1: "hero.slide3.title1",
    title2: "hero.slide3.title2",
    subtitle: "hero.slide3.subtitle",
    image:
      "https://images.unsplash.com/photo-1619682817481-e994891cd1f5?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80",
  },
];

function HomePage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { compareList, addToCompare, removeFromCompare, isInCompare } =
    useCompare();
  const { addToCart, isInCart } = useCart();
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const listingRef = useRef(null);
  const { isAuthenticated } = useContext(AuthContext);

  const [cars, setCars] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isFiltering, setIsFiltering] = useState(false);
  const [error, setError] = useState(null);
  const [userCountry, setUserCountry] = useState("US");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Hero Carousel State
  const [currentSlide, setCurrentSlide] = useState(0);
  const slideIntervalRef = useRef(null);

  const { filters, updateFilters, clearFilters } = useFilters();

  const initialPage = Number.parseInt(searchParams.get("page")) || 1;
  const [pagination, setPagination] = useState({
    page: initialPage,
    pageSize: 16,
    total: 0,
  });

  // Carousel auto-play
  useEffect(() => {
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(slideIntervalRef.current);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
    clearInterval(slideIntervalRef.current);
    slideIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
  };

  const nextSlide = () => goToSlide((currentSlide + 1) % heroSlides.length);
  const prevSlide = () =>
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);

  const prevFiltersRef = useRef(JSON.stringify(filters));
  const [priceAnchorEl, setPriceAnchorEl] = useState(null);
  const openPrice = Boolean(priceAnchorEl);

  // Fetch cars from API
  const fetchCars = async (
    currentFilters = filters,
    page = 1,
    isInitial = false,
  ) => {
    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setIsFiltering(true);
      }

      const MAX_PRICE_LIMIT = 2000000000;
      const params = {
        page,
        limit: pagination.pageSize,
        ...(currentFilters.make &&
          currentFilters.make !== "all" && { make: currentFilters.make }),
        ...(currentFilters.country &&
          currentFilters.country !== "all" && {
            country: currentFilters.country,
          }),
        minPrice: currentFilters.priceRange?.[0] || 0,
        ...(currentFilters.priceRange?.[1] &&
          currentFilters.priceRange[1] < MAX_PRICE_LIMIT && {
            maxPrice: currentFilters.priceRange[1],
          }),
        ...(currentFilters.searchQuery && {
          search: currentFilters.searchQuery.trim(),
        }),
        sortBy: currentFilters.sortBy || "id-desc",
      };

      const urlParams = new URLSearchParams(globalThis.location.search);
      const testCountry = urlParams.get("country");
      if (testCountry) {
        params.testCountry = testCountry;
      }

      const response = await api.get("/cars", { params });

      setCars(response.data.data || []);
      setPagination((prev) => ({
        ...prev,
        page,
        total: response.data.pagination?.total || 0,
      }));

      if (response.data.meta?.userCountry) {
        const country = response.data.meta.userCountry;
        setUserCountry(country);
        if (country !== "SN" && i18n.language !== "en") {
          i18n.changeLanguage("en");
        }
      }
    } catch (err) {
      console.error("Error loading vehicles:", err);
      setError("Impossible de charger les véhicules. Veuillez réessayer.");
      setCars([]);
    } finally {
      setInitialLoading(false);
      setIsFiltering(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await api.get("/favorites/ids");
      setFavorites(response.data || []);
    } catch (err) {
      console.error("Error fetching favorites:", err);
    }
  };

  const handleToggleFavorite = async (e, carId) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated()) {
      return;
    }

    try {
      await api.post("/favorites/toggle", { carId });
      setFavorites((prev) => {
        if (prev.includes(carId)) {
          return prev.filter((id) => id !== carId);
        } else {
          return [...prev, carId];
        }
      });
    } catch (err) {
      console.error("Error toggling favorite:", err);
    }
  };

  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchCars(filters, initialPage, true);
      if (isAuthenticated()) {
        fetchFavorites();
      }
      return;
    }

    const timer = setTimeout(() => {
      fetchCars(filters, 1);
    }, 300);
    return () => clearTimeout(timer);
  }, [JSON.stringify(filters)]);

  // Page change handler
  const handlePageChange = (event, newPage) => {
    if (listingRef.current) {
      listingRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (newPage === 1) {
        newParams.delete("page");
      } else {
        newParams.set("page", newPage.toString());
      }
      return newParams;
    });
    setPagination((prev) => ({ ...prev, page: newPage }));
    fetchCars(filters, newPage);
  };

  // Price filter handler
  const handlePriceChange = (event, newValue) => {
    updateFilters({ ...filters, priceRange: newValue });
  };

  // Clear all filters
  const handleClearFilters = () => {
    clearFilters();
    fetchCars(
      {
        make: "all",
        country: "all",
        priceRange: [0, 2000000000],
        sortBy: "id-desc",
        searchQuery: "",
      },
      1,
    );
  };

  // Loading state
  if (initialLoading) {
    return (
      <Box>
        <Skeleton
          variant="rectangular"
          height={550}
          sx={{ width: "100%", mb: 4 }}
        />
        <Container maxWidth="xl">
          <Skeleton
            variant="rectangular"
            height={100}
            sx={{ borderRadius: 4, mb: 5 }}
          />
          <Grid container spacing={3}>
            {Array.from({ length: 8 }, (_, i) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={`skeleton-card-${i}`}
              >
                <Skeleton
                  variant="rectangular"
                  height={480}
                  sx={{ borderRadius: 4 }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <>
        {/* Compare Floating Button */}
        <CompareFloatingButton show={compareList.length > 0}>
          <Tooltip title={t("compare.view") || "Voir le comparateur"}>
            <Fab
              color="primary"
              aria-label="compare"
              onClick={() => navigate("/compare")}
              variant="extended"
              sx={{ px: 3 }}
            >
              <CompareArrowsIcon sx={{ mr: 1 }} />
              {t("compare.title") || "Comparer"} ({compareList.length})
            </Fab>
          </Tooltip>
        </CompareFloatingButton>

        <Container maxWidth="sm" sx={{ mt: 10 }}>
          <Alert severity="error" variant="filled" sx={{ borderRadius: 3 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            onClick={() => globalThis.location.reload()}
            sx={{ mt: 3 }}
          >
            Réessayer
          </Button>
        </Container>
      </>
    );
  }

  const activeMarket =
    filters.country !== "all" ? filters.country : userCountry;

  return (
    <Box sx={{ minHeight: "100vh", pb: 8, bgcolor: "background.default" }}>
      <SeoComponent />
      <StructuredData type="organization" />
      <StructuredData type="website" />
      <StructuredData
        type="breadcrumb"
        data={{
          items: [{ name: t("nav.home"), url: "https://www.sarayaauto.com" }],
        }}
      />
      {cars.length > 0 && (
        <StructuredData type="itemList" data={{ items: cars }} />
      )}

      {/* Compare Floating Button */}
      <CompareFloatingButton show={compareList.length > 0}>
        <Tooltip title={t("compare.view") || "Voir le comparateur"}>
          <Fab
            color="primary"
            aria-label="compare"
            onClick={() => navigate("/compare")}
            variant="extended"
            sx={{ px: 3 }}
          >
            <CompareArrowsIcon sx={{ mr: 1 }} />
            {t("compare.title") || "Comparer"} ({compareList.length})
          </Fab>
        </Tooltip>
      </CompareFloatingButton>

      {/* Hero Carousel Section */}
      <HeroWrapper>
        <HeroSection>
          {/* Background Images */}
          {heroSlides.map((slide, index) => (
            <HeroBackground
              key={slide.id}
              src={slide.image}
              alt={`Slide ${index + 1}`}
              loading={index === 0 ? "eager" : "lazy"}
              style={{
                opacity: (() => {
                  if (currentSlide !== index) return 0;
                  return theme.palette.mode === "light" ? 0.15 : 0.5;
                })(),
              }}
            />
          ))}

          {/* Navigation Arrows */}
          <CarouselArrow
            onClick={prevSlide}
            sx={{ left: { xs: 8, md: 24 } }}
            aria-label="Slide précédent"
          >
            <ArrowBackIcon />
          </CarouselArrow>
          <CarouselArrow
            onClick={nextSlide}
            sx={{ right: { xs: 8, md: 24 } }}
            aria-label="Slide suivant"
          >
            <ArrowForwardIcon />
          </CarouselArrow>

          {/* Content */}
          <Container maxWidth="lg">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                style={{ textAlign: "center" }}
              >
                <Chip
                  label={
                    t(heroSlides[currentSlide].badge) ||
                    heroSlides[currentSlide].badge
                  }
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    px: 2.5,
                    py: 1,
                    height: "auto",
                    bgcolor: "primary.main",
                    color: "common.white",
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                />
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "1.75rem", sm: "2.5rem", md: "3.25rem" },
                    fontWeight: 700,
                    lineHeight: 1.15,
                    mb: 2.5,
                    letterSpacing: "-0.02em",
                    color:
                      theme.palette.mode === "light"
                        ? "secondary.main"
                        : "text.primary",
                  }}
                >
                  {t(heroSlides[currentSlide].title1) ||
                    heroSlides[currentSlide].title1}
                  <br />
                  <Box component="span" sx={{ color: "primary.main" }}>
                    {t(heroSlides[currentSlide].title2) ||
                      heroSlides[currentSlide].title2}
                  </Box>
                </Typography>
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: "text.secondary",
                    mb: 4,
                    fontWeight: 400,
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.7,
                    fontSize: { xs: "0.95rem", md: "1.1rem" },
                  }}
                >
                  {t(heroSlides[currentSlide].subtitle) ||
                    heroSlides[currentSlide].subtitle}
                </Typography>
              </motion.div>
            </AnimatePresence>

            {/* Carousel Dots */}
            <Stack
              direction="row"
              spacing={1.5}
              justifyContent="center"
              sx={{ mt: 2 }}
            >
              {heroSlides.map((slide, index) => (
                <CarouselDot
                  key={slide.id}
                  active={currentSlide === index}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </Stack>
          </Container>
        </HeroSection>
      </HeroWrapper>

      {/* Main Content */}
      <Container
        maxWidth="xl"
        ref={listingRef}
        sx={{ mt: 0, px: { xs: 2, sm: 3, md: 4 }, scrollMarginTop: "120px" }}
      >
        {/* Filter Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          <FilterBar elevation={0}>
            <Grid container spacing={2} alignItems="center">
              {/* Search */}
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  placeholder={t("filters.search")}
                  value={filters.searchQuery}
                  onChange={(e) =>
                    updateFilters({
                      ...filters,
                      searchQuery: e.target.value,
                    })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "primary.main" }} />
                      </InputAdornment>
                    ),
                  }}
                  size="medium"
                />
              </Grid>

              {/* Brand Select */}
              <Grid item xs={6} md={2}>
                <FormControl
                  fullWidth
                  size="medium"
                  key={`make-${i18n.language}`}
                >
                  <InputLabel id="make-label">{t("filters.brand")}</InputLabel>
                  <Select
                    labelId="make-label"
                    value={filters.make}
                    label={t("filters.brand")}
                    onChange={(e) =>
                      updateFilters({ ...filters, make: e.target.value })
                    }
                  >
                    <MenuItem value="all">{t("filters.all")}</MenuItem>
                    {[
                      "Toyota",
                      "Honda",
                      "Ford",
                      "BMW",
                      "Mercedes",
                      "Audi",
                      "Volkswagen",
                      "Peugeot",
                      "Renault",
                    ].map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Price Filter */}
              <Grid item xs={6} md={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={(e) => setPriceAnchorEl(e.currentTarget)}
                  startIcon={<CurrencyExchangeIcon />}
                  endIcon={
                    <Badge
                      badgeContent={
                        filters.priceRange[0] > 0 ||
                        filters.priceRange[1] < 2000000000
                          ? "!"
                          : 0
                      }
                      color="primary"
                      variant="dot"
                    />
                  }
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    borderColor: alpha(theme.palette.divider, 0.3),
                    color: "text.primary",
                    justifyContent: "space-between",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  {t("filters.budget")}
                </Button>
                <Popover
                  open={openPrice}
                  anchorEl={priceAnchorEl}
                  onClose={() => setPriceAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  transformOrigin={{ vertical: "top", horizontal: "left" }}
                  slotProps={{
                    paper: {
                      sx: { p: 3, width: 320, borderRadius: 4, mt: 1 },
                    },
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                    {t("filters.priceRange")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ mb: 2 }}
                  >
                    {formatPrice(filters.priceRange[0])} -{" "}
                    {filters.priceRange[1] >= 2000000000
                      ? "Illimité"
                      : formatPrice(filters.priceRange[1])}
                  </Typography>
                  <Slider
                    value={filters.priceRange}
                    onChange={handlePriceChange}
                    valueLabelDisplay="auto"
                    min={0}
                    max={2000000000}
                    step={1000000}
                  />
                </Popover>
              </Grid>

              {/* Sort */}
              <Grid item xs={6} md={3}>
                <FormControl
                  fullWidth
                  size="medium"
                  key={`sort-${i18n.language}`}
                >
                  <InputLabel id="sort-label">{t("filters.sortBy")}</InputLabel>
                  <Select
                    labelId="sort-label"
                    value={filters.sortBy}
                    label={t("filters.sortBy")}
                    onChange={(e) =>
                      updateFilters({
                        ...filters,
                        sortBy: e.target.value,
                      })
                    }
                    startAdornment={
                      <InputAdornment position="start">
                        <SortIcon fontSize="small" />
                      </InputAdornment>
                    }
                  >
                    {[
                      {
                        value: "id-desc",
                        label: t("filters.sortOptions.recentDesc"),
                      },
                      {
                        value: "price-asc",
                        label: t("filters.sortOptions.priceAsc"),
                      },
                      {
                        value: "price-desc",
                        label: t("filters.sortOptions.priceDesc"),
                      },
                      {
                        value: "year-desc",
                        label: t("filters.sortOptions.yearDesc"),
                      },
                      {
                        value: "year-asc",
                        label: t("filters.sortOptions.yearAsc"),
                      },
                      {
                        value: "mileage-asc",
                        label: t("filters.sortOptions.mileageAsc"),
                      },
                    ].map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Reset & Count */}
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="flex-end"
                gap={1.5}
              >
                <Button
                  color="error"
                  onClick={handleClearFilters}
                  aria-label={t("filters.reset") || "Reset filters"}
                  sx={{
                    minWidth: 56,
                    height: 56,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                  }}
                >
                  <RestartAltIcon />
                </Button>
                <Chip
                  label={t("filters.vehiclesCount", { count: cars.length })}
                  sx={{
                    fontWeight: 700,
                    height: 56,
                    borderRadius: 3,
                    px: 3,
                    fontSize: "0.95rem",
                    flex: 1,
                    bgcolor: "primary.main",
                    color: "common.white",
                    "& .MuiChip-label": { px: 0 },
                  }}
                />
              </Grid>
            </Grid>
          </FilterBar>
        </motion.div>

        {/* Car Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 3,
            width: "100%",
            "@media (max-width: 600px)": {
              gridTemplateColumns: "1fr",
              gap: 2,
            },
          }}
        >
          <AnimatePresence>
            {cars
              .filter((car) => !isInCart(car.id))
              .map((car) => {
                const imageUrl =
                  car.photos?.[0] ||
                  `https://source.unsplash.com/random/800x600/?${car.make}+${car.model}`;

                return (
                  <Box key={car.id}>
                    <CarCard>
                      <Link
                        to={`/cars/${car.id}`}
                        style={{
                          textDecoration: "none",
                          color: "inherit",
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                        }}
                      >
                        {/* Image */}
                        <Box
                          sx={{
                            position: "relative",
                            overflow: "hidden",
                            flexShrink: 0,
                          }}
                        >
                          <CarImage
                            component="img"
                            loading="lazy"
                            className="car-image"
                            image={imageUrl}
                            alt={`${car.make} ${car.model}`}
                          />
                          {/* Top row: Compare button (left), Price + Favorite (right) */}
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              left: 12,
                              right: 12,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              zIndex: 10,
                            }}
                          >
                            {/* Compare Button */}
                            <IconButton
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (isInCompare(car.id)) {
                                  removeFromCompare(car.id);
                                  setSnackbar({
                                    open: true,
                                    message:
                                      t("compare.removed") ||
                                      "Véhicule retiré du comparateur",
                                    severity: "info",
                                  });
                                } else {
                                  const result = addToCompare(car);
                                  setSnackbar({
                                    open: true,
                                    message: result.message,
                                    severity: result.success
                                      ? "success"
                                      : "warning",
                                  });
                                }
                              }}
                              sx={{
                                bgcolor: "background.paper",
                                color: isInCompare(car.id)
                                  ? "primary.main"
                                  : "text.secondary",
                                boxShadow: 2,
                                "&:hover": {
                                  bgcolor: "background.paper",
                                  color: "primary.main",
                                },
                                transition: "all 0.2s",
                                width: 36,
                                height: 36,
                              }}
                            >
                              <CompareArrowsIcon fontSize="small" />
                            </IconButton>

                            {/* Price + Favorite container */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <PriceBadge className="price-badge">
                                {activeMarket !== "SN" && car.price_usd
                                  ? new Intl.NumberFormat("en-US", {
                                      style: "currency",
                                      currency: "USD",
                                      maximumFractionDigits: 0,
                                    }).format(car.price_usd)
                                  : formatPrice(car.price)}
                              </PriceBadge>
                              {currentUser && (
                                <IconButton
                                  onClick={(e) =>
                                    handleToggleFavorite(e, car.id)
                                  }
                                  sx={{
                                    bgcolor: "background.paper",
                                    color: favorites.includes(car.id)
                                      ? "error.main"
                                      : "text.secondary",
                                    boxShadow: 2,
                                    "&:hover": {
                                      bgcolor: "background.paper",
                                      transform: "scale(1.1)",
                                    },
                                    transition: "all 0.2s",
                                    width: 36,
                                    height: 36,
                                  }}
                                >
                                  {favorites.includes(car.id) ? (
                                    <FavoriteIcon />
                                  ) : (
                                    <FavoriteBorderIcon />
                                  )}
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: "50%",
                              background:
                                "linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 100%)",
                              zIndex: 1,
                            }}
                          />
                        </Box>

                        {/* Content */}
                        <CardContent
                          sx={{
                            flexGrow: 1,
                            p: 2.5,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {/* Title */}
                          <Typography
                            variant="h6"
                            fontWeight={700}
                            sx={{
                              lineHeight: 1.3,
                              mb: 1,
                              fontSize: "1.05rem",
                              height: "2.7em",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              color:
                                theme.palette.mode === "light"
                                  ? "secondary.main"
                                  : "text.primary",
                            }}
                          >
                            {car.year} {car.make} {car.model}
                          </Typography>

                          {/* Specs - horizontal row */}
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.75,
                              mb: 2,
                              flexWrap: "wrap",
                            }}
                          >
                            <SpecBadge
                              sx={{
                                flex: 1,
                                justifyContent: "center",
                                minWidth: 0,
                              }}
                            >
                              <SpeedIcon
                                sx={{
                                  fontSize: 16,
                                  color: "primary.main",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {car.mileage?.toLocaleString("fr-FR")} mi
                              </Typography>
                            </SpecBadge>
                            <SpecBadge
                              sx={{
                                flex: 1,
                                justifyContent: "center",
                                minWidth: 0,
                              }}
                            >
                              <LocalGasStationIcon
                                sx={{
                                  fontSize: 16,
                                  color: "primary.main",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {getTranslatedFuelType(car.fuelType, t)}
                              </Typography>
                            </SpecBadge>
                            <SpecBadge
                              sx={{
                                flex: 1,
                                justifyContent: "center",
                                minWidth: 0,
                              }}
                            >
                              <SettingsSuggestIcon
                                sx={{
                                  fontSize: 16,
                                  color: "primary.main",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                                sx={{ fontSize: "0.8rem" }}
                              >
                                {getTranslatedTransmission(car.transmission, t)}
                              </Typography>
                            </SpecBadge>
                          </Box>

                          {/* Action Buttons Row */}
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: "auto" }}
                          >
                            <Button
                              className="view-btn"
                              variant="outlined"
                              fullWidth
                              endIcon={<ArrowForwardIcon />}
                              sx={{
                                borderRadius: 10,
                                textTransform: "none",
                                fontWeight: 700,
                                py: 1.2,
                                fontSize: "0.85rem",
                                borderWidth: 1.5,
                                borderColor: alpha(
                                  theme.palette.primary.main,
                                  0.25,
                                ),
                                color: "text.primary",
                                transition:
                                  "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                "&:hover": {
                                  borderWidth: 1.5,
                                  borderColor: "transparent",
                                },
                              }}
                            >
                              {t("car.viewDetails")}
                            </Button>

                            {!currentUser || !isAuthenticated() ? null : (
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (isInCart(car.id)) {
                                    navigate("/cart");
                                  } else {
                                    addToCart(car);
                                    setSnackbar({
                                      open: true,
                                      message:
                                        t("car.details.saved") ||
                                        "Ajouté au panier",
                                      severity: "success",
                                    });
                                  }
                                }}
                                sx={{
                                  borderRadius: 10,
                                  bgcolor: isInCart(car.id)
                                    ? "primary.main"
                                    : alpha(theme.palette.primary.main, 0.08),
                                  color: isInCart(car.id)
                                    ? "white"
                                    : "primary.main",
                                  width: 44,
                                  height: 44,
                                  flexShrink: 0,
                                  "&:hover": {
                                    bgcolor: isInCart(car.id)
                                      ? "primary.dark"
                                      : alpha(theme.palette.primary.main, 0.15),
                                  },
                                  transition: "all 0.2s",
                                }}
                              >
                                {isInCart(car.id) ? (
                                  <ShoppingCartIcon fontSize="small" />
                                ) : (
                                  <AddShoppingCartIcon fontSize="small" />
                                )}
                              </IconButton>
                            )}
                          </Stack>
                        </CardContent>
                      </Link>
                    </CarCard>
                  </Box>
                );
              })}
          </AnimatePresence>
        </Box>

        {/* Pagination */}
        {Math.ceil(pagination.total / pagination.pageSize) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <Pagination
              count={Math.ceil(pagination.total / pagination.pageSize)}
              page={pagination.page}
              onChange={handlePageChange}
              size="large"
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {/* Empty State */}
        {cars.length === 0 && !initialLoading && !isFiltering && (
          <Box textAlign="center" py={10}>
            <DirectionsCarIcon
              sx={{ fontSize: 80, color: "text.disabled", mb: 3, opacity: 0.4 }}
            />
            <Typography
              variant="h5"
              color="text.secondary"
              fontWeight={700}
              gutterBottom
            >
              {t("filters.noResults") || "Aucun véhicule trouvé"}
            </Typography>
            <Typography color="text.secondary" mb={4}>
              {t("filters.tryDifferent") ||
                "Essayez de modifier vos filtres pour voir plus de résultats."}
            </Typography>
            <Button
              variant="contained"
              onClick={handleClearFilters}
              startIcon={<RestartAltIcon />}
              size="large"
            >
              {t("filters.reset") || "Réinitialiser la recherche"}
            </Button>
          </Box>
        )}
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2, boxShadow: 3 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Services Section */}
      <Box
        sx={{
          mt: 12,
          py: 10,
          background:
            theme.palette.mode === "light"
              ? alpha(theme.palette.slate[50], 0.8)
              : "transparent",
        }}
      >
        <Container maxWidth="lg">
          {/* Services */}
          <Box mb={12}>
            <Typography
              variant="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "2.5rem" },
                mb: 2,
                color:
                  theme.palette.mode === "light"
                    ? "secondary.main"
                    : "text.primary",
              }}
            >
              {t("home.services.title")}
            </Typography>
            <Typography
              variant="body1"
              align="center"
              color="text.secondary"
              sx={{ mb: 6, maxWidth: 550, mx: "auto", fontSize: "1.1rem" }}
            >
              {t("home.services.subtitle")}
            </Typography>

            <Stack
              spacing={3}
              direction={{ xs: "column", md: "row" }}
              alignItems="stretch"
            >
              {[
                {
                  key: "sale",
                  icon: <SellIcon fontSize="large" />,
                  color: theme.palette.primary.main,
                },
                {
                  key: "purchase",
                  icon: <ShoppingBagIcon fontSize="large" />,
                  color: theme.palette.secondary.main,
                },
                {
                  key: "advice",
                  icon: <SupportAgentIcon fontSize="large" />,
                  color: "#10B981",
                },
              ].map((service) => (
                <Box key={service.key} sx={{ flex: 1, display: "flex" }}>
                  <ServiceCard
                    elevation={0}
                    sx={{ width: "100%", textAlign: "center" }}
                  >
                    <IconWrapper color={service.color}>
                      {service.icon}
                    </IconWrapper>
                    <Typography
                      variant="h5"
                      component="h3"
                      fontWeight={700}
                      gutterBottom
                    >
                      {t(`home.services.${service.key}`).split(":")[0]}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      lineHeight={1.7}
                    >
                      {t(`home.services.${service.key}`).split(":")[1]}
                    </Typography>
                  </ServiceCard>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Why Choose Us */}
          <Box mb={12}>
            <Typography
              variant="h2"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "2.5rem" },
                mb: 4,
                color:
                  theme.palette.mode === "light"
                    ? "secondary.main"
                    : "text.primary",
              }}
            >
              {t("home.whyChoose.title")}
            </Typography>

            <Stack
              spacing={3}
              direction={{ xs: "column", md: "row" }}
              alignItems="stretch"
            >
              {[
                {
                  key: "trust",
                  icon: <VerifiedUserIcon fontSize="large" />,
                  color: theme.palette.primary.main,
                },
                {
                  key: "quality",
                  icon: <WorkspacePremiumIcon fontSize="large" />,
                  color: theme.palette.secondary.main,
                },
                {
                  key: "choice",
                  icon: <GarageIcon fontSize="large" />,
                  color: "#F59E0B",
                },
              ].map((item) => (
                <Box key={item.key} sx={{ flex: 1, display: "flex" }}>
                  <ServiceCard
                    elevation={0}
                    sx={{ width: "100%", textAlign: "center" }}
                  >
                    <IconWrapper color={item.color}>{item.icon}</IconWrapper>
                    <Typography
                      variant="h5"
                      component="h3"
                      fontWeight={700}
                      gutterBottom
                    >
                      {t(`home.whyChoose.${item.key}`).split(":")[0]}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      lineHeight={1.7}
                    >
                      {t(`home.whyChoose.${item.key}`).split(":")[1]}
                    </Typography>
                  </ServiceCard>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* CTA Section */}
          <Paper
            sx={{
              p: { xs: 5, md: 7 },
              borderRadius: 5,
              background: theme.palette.primary.main,
              color: "common.white",
              boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.35)}`,
              position: "relative",
              overflow: "hidden",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "-15%",
                right: "-5%",
                opacity: 0.1,
              }}
            >
              <StarIcon sx={{ fontSize: 250 }} />
            </Box>
            <Box
              sx={{
                position: "absolute",
                bottom: "-15%",
                left: "-5%",
                opacity: 0.1,
              }}
            >
              <StarIcon sx={{ fontSize: 200 }} />
            </Box>

            <Box position="relative" zIndex={1}>
              <Typography
                variant="h2"
                fontWeight={700}
                gutterBottom
                sx={{ fontSize: { xs: "2rem", md: "2.75rem" } }}
              >
                {t("home.explore.title")}
              </Typography>
              <Typography
                variant="h6"
                component="p"
                sx={{
                  mb: 5,
                  opacity: 0.9,
                  lineHeight: 1.7,
                  maxWidth: 700,
                  mx: "auto",
                  fontWeight: 400,
                }}
              >
                {t("home.explore.text")}
              </Typography>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/contact"
                sx={{
                  bgcolor: "common.white",
                  color: "primary.main",
                  borderRadius: 3,
                  px: 5,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.95),
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 32px rgba(0,0,0,0.2)",
                  },
                }}
              >
                {t("nav.contact")}
              </Button>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

export default HomePage;
